/**
 * TRF's one clock, browser side (#2196, #2198). Mirrors backend/src/lib/trfTime.js:
 * every code expiry and cycle deadline is 23:59:59.999 Pacific on a calendar
 * date (ruling 2026-10-05). The server does the arithmetic; this file names
 * those dates the way the server means them: "today" in Pacific, and an
 * expiry or deadline as its Pacific date, labelled "midnight PT".
 *
 * #2741 (Mario, 2026-10-12): everything ELSE a staffer reads (when an
 * application was submitted, when the AI review ran, when a notice went out)
 * is shown in the staffer's own timezone: their platform setting
 * (`User.profile.timezone`, handed in through `setViewerTimeZone`), else the
 * browser's. The cut-offs stay Pacific because the cut-off IS Pacific.
 */
export const TRF_TIMEZONE = 'America/Los_Angeles';

/** What staff read beside every expiry and deadline. */
export const MIDNIGHT_PT = 'midnight PT';

const ymdFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: TRF_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const displayFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: TRF_TIMEZONE,
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

/** The Pacific calendar date (YYYY-MM-DD) an instant falls on. */
export function trfDateOf(instant: Date | string = new Date()): string {
  const parts = ymdFormatter.formatToParts(new Date(instant));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Today in Pacific. */
export function trfToday(now: Date = new Date()): string {
  return trfDateOf(now);
}

/** `ymd` moved by `days` calendar days. */
export function addCalendarDays(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** Whole calendar days from today in Pacific to `ymd` (negative when past). */
export function daysFromToday(ymd: string, now: Date = new Date()): number {
  const toUtc = (s: string) => {
    const [y, m, d] = s.split('-').map(Number);
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((toUtc(ymd) - toUtc(trfToday(now))) / 86_400_000);
}

/** An instant as its Pacific date: "Nov 15, 2026". */
export function formatTrfDate(instant: Date | string): string {
  return displayFormatter.format(new Date(instant));
}

/** A YYYY-MM-DD as staff read it: "Nov 15, 2026". */
export function formatCalendarDate(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  // Noon UTC is the same calendar date in Pacific at either offset.
  return displayFormatter.format(new Date(Date.UTC(y, m - 1, d, 12)));
}

/** An expiry or deadline instant: "Nov 15, 2026, midnight PT". */
export function formatTrfDeadline(instant: Date | string): string {
  return `${formatTrfDate(instant)}, ${MIDNIGHT_PT}`;
}

// ── The viewer's own timezone (#2741) ───────────────────────────────────

let viewerZoneSetting: string | null = null;

function isUsableZone(zone: unknown): zone is string {
  if (typeof zone !== 'string' || zone.trim() === '') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

function browserZone(): string | null {
  try {
    const zone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isUsableZone(zone) ? zone : null;
  } catch {
    return null;
  }
}

/**
 * The signed-in person's timezone setting (`User.profile.timezone`). Called
 * when the session adopts a user, and with null on sign-out. An empty or
 * unknown zone falls back to the browser's.
 */
export function setViewerTimeZone(zone: string | null | undefined): void {
  viewerZoneSetting = isUsableZone(zone) ? zone : null;
}

/** The zone staff read event times in: their setting, else the browser's, else Pacific. */
export function viewerTimeZone(): string {
  return viewerZoneSetting ?? browserZone() ?? TRF_TIMEZONE;
}

type FormatterKind = 'date' | 'monthDay' | 'time' | 'ymd';
const FORMATTER_OPTIONS: Record<FormatterKind, Intl.DateTimeFormatOptions> = {
  date: { month: 'short', day: 'numeric', year: 'numeric' },
  monthDay: { month: 'short', day: 'numeric' },
  time: { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' },
  ymd: { year: 'numeric', month: '2-digit', day: '2-digit' },
};
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatterFor(zone: string, kind: FormatterKind): Intl.DateTimeFormat {
  const key = `${kind}|${zone}`;
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', { timeZone: zone, ...FORMATTER_OPTIONS[kind] });
    formatterCache.set(key, formatter);
  }
  return formatter;
}

/**
 * "7:00 PM PT" or "10:00 PM ET". US zones go by the name people say, so PDT
 * and PST are both "PT". Elsewhere it is what Intl gives ("GMT+1", "HST").
 */
function timeWithZone(d: Date, zone: string): string {
  const parts = formatterFor(zone, 'time').formatToParts(d);
  const name = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  const time = parts
    .filter((p) => p.type !== 'timeZoneName')
    .map((p) => p.value)
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
  const spoken = name.replace(/^(E|C|M|P|AK)[SD]T$/, '$1T');
  return spoken ? `${time} ${spoken}` : time;
}

// ── Event dates on the application page (trf-01 v1.139.1, 2026-10-11) ────
//
// One decline showed "Declined Oct 11" (the UTC day of `state_entered_at`),
// "recorded Oct 10" (the viewer's day of `recorded_at`) and "notice sent
// 10/10" (the viewer's numeric locale date of `notice_sent_at`): three
// clocks, three styles. Every date about an application event now goes
// through these, in one style and on one clock: the viewer's own (#2741).
// The queue, the application page and the review panel therefore name the
// same day for the same event (#2704).

function validInstant(instant: Date | string | null | undefined): Date | null {
  if (instant === null || instant === undefined || instant === '') return null;
  const d = instant instanceof Date ? instant : new Date(instant);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * #2704: a calendar day rather than an instant — a date-only string, or a
 * value at exactly UTC midnight (imported or staff-entered `applied_at`). It
 * keeps its own day; only a true instant moves to the viewer's clock. The same
 * rule as the backend one-pager's `isCalendarDay`
 * (backend/src/services/committeeOnePager/content.js), so the staff page and
 * the one-pager print the same day (#2754 review).
 */
export function isCalendarDay(value: Date | string | null | undefined): boolean {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return true;
  if (value === null || value === undefined || value === '') return false;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return false;
  return d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0;
}

/** The day an event happened, in the viewer's timezone ("Oct 10, 2026"), or null. */
export function trfEventDate(instant: Date | string | null | undefined): string | null {
  const d = validInstant(instant);
  return d ? formatterFor(viewerTimeZone(), 'date').format(d) : null;
}

/** An event with its time, in the viewer's timezone ("Oct 10, 2026, 10:00 PM ET"), or null. */
export function trfEventDateTime(instant: Date | string | null | undefined): string | null {
  const d = validInstant(instant);
  if (!d) return null;
  const zone = viewerTimeZone();
  return `${formatterFor(zone, 'date').format(d)}, ${timeWithZone(d, zone)}`;
}

/**
 * A calendar day stored as UTC midnight (a committee meeting date), in the
 * same style: the day that was typed, never shifted by a zone. Null when
 * there is no usable value.
 */
export function trfCalendarDay(value: Date | string | null | undefined): string | null {
  const d = validInstant(value);
  return d ? formatCalendarDate(d.toISOString().slice(0, 10)) : null;
}

/**
 * #2756: a value that may be either a picked calendar day (stored as UTC
 * midnight, e.g. a poll's planned close typed as a date) or a real instant.
 * A calendar day keeps the day that was picked; an instant names the
 * viewer's day (`isCalendarDay` decides, as on the application page).
 */
export function trfEventOrCalendarDay(value: Date | string | null | undefined): string | null {
  return isCalendarDay(value) ? trfCalendarDay(value) : trfEventDate(value);
}

/**
 * #2822: whole calendar days from `instant` to `now`, both read on the
 * viewer's clock (the one `trfEventDate` prints the day in), so a count and
 * the printed date can never disagree. A request filed at 8 PM yesterday is
 * one day old at 9 AM, not "today" because fewer than 24 hours have passed.
 * Never negative; null when `instant` is unusable.
 */
export function viewerCalendarDaysSince(instant: Date | string | null | undefined, now: Date = new Date()): number | null {
  const d = validInstant(instant);
  if (!d) return null;
  const zone = viewerTimeZone();
  const dayNumber = (x: Date): number => {
    const parts = formatterFor(zone, 'ymd').formatToParts(x);
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
    return Date.UTC(get('year'), get('month') - 1, get('day')) / 86_400_000;
  };
  return Math.max(0, Math.round(dayNumber(now) - dayNumber(d)));
}

/** 2026-10-11 Option A: a short day for a chip ("Oct 9"), in the viewer's timezone, or null. */
export function trfMonthDay(instant: Date | string | null | undefined): string | null {
  const d = validInstant(instant);
  return d ? formatterFor(viewerTimeZone(), 'monthDay').format(d) : null;
}
